/* eslint-disable react/prop-types */
//
import React, { memo, useState } from 'react';
import { useSelector } from 'react-redux';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { authSelectors } from 'app-components/flux/auth';

import Dump from 'components/Dump';
import QButton from 'components/QButton';

import AddShareDialog from './AddShareDialog';
import OwnedDatasetMenu from './OwnedDatasetMenu';
import SharedDatasetMenu from './SharedDatasetMenu';
import StatusChip from './StatusChip';
import { getNameData, getStatusText } from './helpers';
import useShareCounts from './useShareCounts';

const useStyles = makeStyles()((theme) => ({
  avatarRoot: {
    '&:not(:first-of-type)': {
      marginLeft: '-12px',
    },
  },
  avatarToolip: {
    background: theme.palette.icon.main,
    color: theme.palette.background.paper,
  },
  title: {
    width: 275,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&.active': {
      width: 230,
    },
  },
}));

// type Props = {
//   dataset: datasetsTypes.Dataset,
//   shares: ?any;
// id:
// ?any
// disabled:
// ?boolean
// }

function DatasetCard(props) {
  const { dataset, shares, disabled, ...rest } = props;

  const [showAddShareDialog, setShowAddShareDialog] = useState(false);
  const currentDatasetId = useSelector(authSelectors.getDatasetId);

  const shareCounts = useShareCounts(shares);
  const [sharesUsed, sharesAllowed] = shareCounts;
  const sharesLeft = Math.max(sharesAllowed - sharesUsed, 0);

  const { classes, cx } = useStyles();
  const theme = useTheme();

  const avatarsData = (() => {
    const data = shares
      ?.filter((share) => share.state === 'ACTIVE')
      .map((share) => {
        const nameData = getNameData(share.granteeFirstName, share.granteeLastName, share.granteeEmail);
        return {
          key: share.id,
          initials: nameData.initials,
          name: nameData.name,
          share,
        };
      })
      .sort((a, b) => a.initials.localeCompare(b.initials));

    // add owners data as first avatar
    if (dataset.shared && shares) {
      const share = shares[0];
      const nameData = getNameData(share.grantorFirstName, share.grantorLastName, share.grantorEmail);
      data.unshift({
        key: dataset.id,
        initials: nameData?.initials,
        name: nameData?.name,
        share,
      });
    }
    return data && data.length > 0 ? data : null;
  })();
  const ownerIdentifier = (() => {
    if (dataset.shared) {
      const share = shares && shares[0];
      if (share) {
        const nameData = getNameData(share.grantorFirstName, share.grantorLastName, share.grantorEmail);
        return nameData?.name;
      }
      return null;
    }
    return 'You';
  })();

  const renderDatasetMenu = () =>
    !dataset.shared ? (
      <OwnedDatasetMenu dataset={dataset} shares={shares} shareCounts={shareCounts} />
    ) : (
      <SharedDatasetMenu dataset={dataset} shares={shares} />
    );

  const renderAvatarTooltip = (avatarData) => (
    <Typography variant='captionRegular' sx={{ color: 'inherit' }}>
      {avatarData.name}
      <br />
      {getStatusText(avatarData.share, 'Last accessed')}
    </Typography>
  );

  return (
    <>
      {showAddShareDialog && (
        <AddShareDialog dataset={dataset} onClose={() => setShowAddShareDialog(false)} shareCounts={shareCounts} />
      )}

      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 350,
          height: 219,
          my: 2,
          marginLeft: 0,
          marginRight: 4,
          position: 'relative',
        }}
        disabled={disabled}
        {...rest}
      >
        <Dump obj={dataset} />
        <CardHeader
          action={
            <>
              {dataset.id === currentDatasetId && (
                <StatusChip label='Active' sx={{ color: theme.palette.primary.main }} />
              )}
              {renderDatasetMenu()}
            </>
          }
          title={
            <Typography
              id={`${props.id}-name`}
              variant='headlineRegular'
              className={cx(classes.title, dataset.id === currentDatasetId ? 'active' : '')}
            >
              {dataset.name}
            </Typography>
          }
          subheader={ownerIdentifier}
          sx={{ p: 3 }}
          disabled={disabled}
        />
        <CardContent sx={{ display: 'flex', flex: '1' }}></CardContent>
        <CardActions sx={{ p: 3 }}>
          {avatarsData &&
            avatarsData.length &&
            avatarsData.map((avatarData) => (
              <Tooltip
                classes={{ tooltip: classes.avatarToolip }}
                key={avatarData.key}
                placement='top-start'
                title={renderAvatarTooltip(avatarData)}
              >
                <Box
                  key={avatarData.share.id}
                  sx={{
                    padding: '2px',
                    background: `linear-gradient(45deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
                    borderRadius: '50%',
                  }}
                >
                  <Avatar
                    size='small'
                    aria-label={avatarData.initials}
                    classes={{
                      root: classes.avatarRoot,
                    }}
                    sx={{
                      background: `linear-gradient(45deg, ${theme.palette.secondary.light}, ${theme.palette.info.light})`,
                      color: theme.palette.text.primary,
                      width: 36,
                      height: 36,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <Typography variant='paragraphMedium'>{avatarData.initials}</Typography>
                  </Avatar>
                </Box>
              </Tooltip>
            ))}
          {!dataset.shared && (
            <QButton disabled={!sharesLeft || disabled} onClick={() => setShowAddShareDialog(true)} variant='text'>
              + Invite {!!sharesUsed && !!sharesLeft && `(${sharesLeft})`}
            </QButton>
          )}
        </CardActions>
      </Card>
    </>
  );
}

export default memo(DatasetCard);
